import React, { useEffect } from 'react';
import { Form, DatePicker, Button, Col, Row, notification } from 'antd';
import moment from 'moment';
import { getCertificateExpiration, setCertificateValidity } from '@app/api/prImportFreeSales.api';
import { setDatesSet } from '@app/utils/dateSetStore';

interface CertificateValidityFormProps {
  form: any;
  pr_id: string | null;
  status: string | null;
}

const CertificateValidityForm: React.FC<CertificateValidityFormProps> = ({ form, pr_id, status }) => {
  const startDate = Form.useWatch('date', form);
  const endDate = Form.useWatch('expires_at', form);
  const isEditable = status === 'PENDING_COMMITTEE_REVIEW' || 'PENDING_DVRA_FINAL_APPROVAL';

  const [isFetchingEndDate, setIsFetchingEndDate] = React.useState(false);

  useEffect(() => {
    const fetchEndDate = async () => {
      if (pr_id && startDate) {
        setIsFetchingEndDate(true);
        try {
          const response = await getCertificateExpiration(Number(pr_id));
          if (response) {
            const expiryMoment = moment(response, 'YYYY-MM-DD');
            form.setFieldsValue({ expires_at: expiryMoment });
          } else {
            notification.warning({
              message: 'Could not auto-set end date',
              description: 'Expiration date not returned from API.',
            });
          }
        } catch (error) {
          notification.error({
            message: 'Error fetching expiration date',
            description: (error as Error).message,
          });
        } finally {
          setIsFetchingEndDate(false);
        }
      }
    };

    fetchEndDate();
  }, [startDate, pr_id, form]);

  const isButtonDisabled = !startDate || !endDate;

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      const CertificateDates = {
        date: values.date.format('YYYY-MM-DD'),
        expires_at: values.expires_at.format('YYYY-MM-DD'),
      };

      if (pr_id) {
        await setCertificateValidity(Number(pr_id), CertificateDates);
        notification.success({
          message: 'Certificate Validity Set',
          description: 'Successfully updated certificate validity dates.',
        });
        setDatesSet(Number(pr_id), true);
      } else {
        notification.warning({
          message: 'Missing Product Registration ID',
          description: 'Please provide a valid PR ID to proceed.',
        });
      }
    } catch (error: any) {
      notification.error({
        message: 'Failed to Set Validity',
        description: error.message || 'Something went wrong.',
      });
    }
  };

  return (
    <>
      <br />
      <h4 style={{ fontWeight: '500' }}>Set Certificate Validity</h4>
      <br />
      <Row gutter={16}>
        <Col xs={24} md={8} lg={8}>
          <Form.Item label="Start Date" name="date" rules={[{ required: true, message: 'Start date is required' }]}>
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => current && current < moment().startOf('day')}
              disabled={!isEditable}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8} lg={8}>
          <Form.Item
            label="End Date"
            name="expires_at"
            dependencies={['date']}
            rules={[{ required: true, message: 'End date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} disabled={!isEditable || isFetchingEndDate || !startDate} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" size="small" onClick={handleSubmit} disabled={isButtonDisabled || !isEditable}>
          Set Validity
        </Button>
      </Form.Item>
    </>
  );
};

export default CertificateValidityForm;
